import * as React from 'react';
import { getConfigOrThrow } from 'gossamer-front-end';

export const useStripePaymentIntent = ({
  open,
  ticketId,
  bookingPaymentTermsId,
}: {
  open: boolean;
  ticketId: number | null;
  bookingPaymentTermsId: number | null;
}) => {
  const [clientSecret, setClientSecret] = React.useState<string>('');
  const [intentError, setIntentError] = React.useState<string>('');
  const [isCreatingIntent, setIsCreatingIntent] = React.useState(false);

  const intentKey = ticketId && bookingPaymentTermsId ? `${ticketId}:${bookingPaymentTermsId}` : '';
  const lastIntentKeyRef = React.useRef<string>('');

  React.useEffect(() => {
    if (!open) return;
    if (!intentKey) return;
    if (lastIntentKeyRef.current !== intentKey) {
      setClientSecret('');
      setIntentError('');
    }
  }, [intentKey, open]);

  React.useEffect(() => {
    if (!open) return;
    if (!ticketId || !bookingPaymentTermsId) return;
    if (clientSecret && lastIntentKeyRef.current === intentKey) return;
    lastIntentKeyRef.current = intentKey;

    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 15_000);
    const baseUrl = getConfigOrThrow('gossamer-be-origin');

    (async () => {
      try {
        setIsCreatingIntent(true);
        setIntentError('');
        const response = await fetch(`${baseUrl}/gossamer/main/create-payment-intent`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ticketId,
            bookingPaymentTermsId,
          }),
          signal: controller.signal,
        });
        const json = await response.json().catch(() => ({}));
        if (!response.ok || !json?.clientSecret) {
          throw new Error(json?.error || json?.message || 'Failed to create payment intent.');
        }
        setClientSecret(String(json.clientSecret));
      } catch (error: any) {
        const message = error?.message === 'The user aborted a request.' ? 'Payment request timed out.' : error?.message;
        setIntentError(message || 'Failed to create payment intent.');
      } finally {
        setIsCreatingIntent(false);
        window.clearTimeout(timeoutId);
      }
    })();

    return () => {
      window.clearTimeout(timeoutId);
      controller.abort();
    };
  }, [open, ticketId, bookingPaymentTermsId, clientSecret, intentKey]);

  return { clientSecret, intentError, isCreatingIntent };
};
